import {
  type ComponentProps,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Box } from "@hightouch/htui";
import { rawAgentFileUrlFallback } from "@hightouch/lib/ad-studio/html-editor/resolve-ad-refs";
import { EmailPreviewIframe } from "src/components/preview-canvas/email-preview-iframe";
import { prepareWebPageHtml } from "src/pages/ad-studio/html-ad-editor/prepare-ad-content-for-display";
import type { PreviewDevice } from "src/pages/lifecycle-studio/components/email-renderer";
import { useRegionConfig } from "src/utils/use-region-config";

export const WEB_PAGE_DESKTOP_WIDTH = 1280;
export const WEB_PAGE_MOBILE_WIDTH = 375;
/** The desktop fold the grid thumbnail crops to. */
const THUMBNAIL_VIEWPORT_HEIGHT = 800;
/** Caps the "page" variant: a `100vh` section grows with the frame, so its measured height would never settle. */
export const PAGE_MAX_HEIGHT = 12000;

export type WebPageContent = {
  html: string;
  /** `ad:<filename>` ref to its signed URL. */
  assets: Record<string, string>;
};

type WebPagePreviewProps = {
  content: WebPageContent;
  /** The file version the content belongs to. */
  versionId: string | null;
  /**
   * "thumbnail": a non-interactive desktop fold scaled to the container width.
   * "page": the whole page, non-interactive, scaled to the container width.
   * "full": scrollable, filling the caller's container.
   */
  variant: "thumbnail" | "page" | "full";
  /** Lays the page out at this device's width; narrower containers scale it down. */
  device?: PreviewDevice;
  onDocumentReady?: ComponentProps<
    typeof EmailPreviewIframe
  >["onDocumentReady"];
  /** The "page" variant takes clicks and follows links like "full", for a host that draws over it. */
  isInteractive?: boolean;
  /** Reports the "page" variant's height, capped at `PAGE_MAX_HEIGHT`. */
  onPageHeight?: (height: number) => void;
  /** Loads raster assets resized to this many CSS px wide, for a preview drawn far smaller than the page. */
  imageWidth?: number;
};

/**
 * Renders a web page's HTML document through the email preview iframe,
 * laid out at the device's width and scaled down to fit, as `ScaledEmailPreview` does.
 */
export function WebPagePreview({
  content: { html: source, assets: latestAssets },
  versionId,
  variant,
  device = "desktop",
  onDocumentReady,
  isInteractive = false,
  onPageHeight,
  imageWidth,
}: WebPagePreviewProps) {
  const viewportWidth =
    device === "mobile" ? WEB_PAGE_MOBILE_WIDTH : WEB_PAGE_DESKTOP_WIDTH;
  const { apiEndpoint } = useRegionConfig();
  const containerRef = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState<{ width: number; height: number } | null>(
    null,
  );
  const isThumbnail = variant === "thumbnail";
  const isPage = variant === "page";
  const isStatic = isThumbnail || isPage;
  const isInert = isStatic && !isInteractive;
  const [pageHeight, setPageHeight] = useState<number>();

  // Every read re-presigns the URLs; they last 7 days, so keeping a version's first ones avoids reloading the iframe on each poll.
  const pinned = useRef<{ key: string; assets: Record<string, string> }>();
  const key = `${versionId}:${source}`;
  if (pinned.current?.key !== key) {
    pinned.current = { key, assets: latestAssets };
  }
  const assets = pinned.current.assets;

  const html = useMemo(
    () => prepareWebPageHtml(source, assets, apiEndpoint, { imageWidth }),
    [source, assets, apiEndpoint, imageWidth],
  );
  const documentReady = useMemo(
    () =>
      imageWidth
        ? (doc: Document) => {
            const teardown = onDocumentReady?.(doc);
            const stopFallback = fallBackFromFailedResizes(doc);
            return () => {
              stopFallback();
              teardown?.();
            };
          }
        : onDocumentReady,
    [imageWidth, onDocumentReady],
  );

  useEffect(() => {
    if (pageHeight !== undefined)
      onPageHeight?.(Math.min(pageHeight, PAGE_MAX_HEIGHT));
  }, [pageHeight, onPageHeight]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const measure = () =>
      setSize({ width: container.clientWidth, height: container.clientHeight });
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  const scale = size ? Math.min(size.width / viewportWidth, 1) : 0;
  const frameHeight = isThumbnail
    ? THUMBNAIL_VIEWPORT_HEIGHT
    : isPage
      ? Math.min(pageHeight ?? THUMBNAIL_VIEWPORT_HEIGHT, PAGE_MAX_HEIGHT)
      : size && scale > 0
        ? size.height / scale
        : 0;

  return (
    <Box
      ref={containerRef}
      width="100%"
      height={isStatic ? undefined : "100%"}
      overflow="hidden"
      bg="white"
      // Keeps the thumbnail iframe out of the tab order; React 18 has no typed `inert` prop.
      {...(isInert ? { inert: "" } : {})}
      style={
        isThumbnail
          ? { aspectRatio: `${viewportWidth} / ${THUMBNAIL_VIEWPORT_HEIGHT}` }
          : undefined
      }
    >
      {scale > 0 && (
        <Box
          width={`${viewportWidth * scale}px`}
          height={`${frameHeight * scale}px`}
          mx="auto"
        >
          <Box
            width={`${viewportWidth}px`}
            height={`${frameHeight}px`}
            transform={scale < 1 ? `scale(${scale})` : undefined}
            transformOrigin="top left"
            pointerEvents={isInert ? "none" : undefined}
          >
            <EmailPreviewIframe
              html={html}
              title="Web page preview"
              linkBehavior={isInert ? "disable" : "retarget"}
              width={viewportWidth}
              maxWidth="none"
              fitToContent={isPage}
              onMeasureHeight={isPage ? setPageHeight : undefined}
              onDocumentReady={documentReady}
            />
          </Box>
        </Box>
      )}
    </Box>
  );
}

/** The resizer rejects some sources (one past its pixel cap, say), so a resized image that fails loads its original instead. */
function fallBackFromFailedResizes(doc: Document): () => void {
  const fallBack = (image: HTMLImageElement) => {
    const original = rawAgentFileUrlFallback(image.currentSrc || image.src);
    if (original) image.src = original;
  };
  // The document is ready only once its images settled, so some have already failed.
  for (const image of Array.from(doc.images)) {
    if (image.complete && image.naturalWidth === 0) fallBack(image);
  }
  const onError = (event: Event) => {
    const view = doc.defaultView;
    if (view && event.target instanceof view.HTMLImageElement)
      fallBack(event.target);
  };
  doc.addEventListener("error", onError, true);
  return () => doc.removeEventListener("error", onError, true);
}
